import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Rocket, Sparkles, Star, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { trackJoinClick } from "@/lib/analytics";

const SERVER_ICON_FALLBACK =
  "https://images.unsplash.com/photo-1614680376739-414d95ff43df?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxkaXNjb3JkJTIwc2VydmVyJTIwZ2FtaW5nfGVufDB8fHx8MTc4Mjk5NDE5Nnww&ixlib=rb-4.1.0&q=85";

// Deterministic hash → unique gradient per server. No external image
// dependency, always loads instantly, and no two adjacent servers share
// the same banner unless they hash to the same value (extremely unlikely).
function bannerGradient(id) {
  if (!id) return "linear-gradient(135deg, #1a1a2e, #16213e)";
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = ((hash << 5) - hash + id.charCodeAt(i)) | 0;
  }
  const h1 = Math.abs(hash) % 360;
  const h2 = (h1 + 40 + (Math.abs(hash >> 8) % 60)) % 360;
  const h3 = (h1 + 200) % 360;
  return `linear-gradient(135deg, hsl(${h1}, 65%, 22%) 0%, hsl(${h2}, 60%, 32%) 55%, hsl(${h3}, 70%, 18%) 100%)`;
}

function relTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (!then) return "";
  const s = Math.floor((Date.now() - then) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function ServerCard({ server, index = 0, onBump }) {
  const [bannerBroken, setBannerBroken] = useState(false);
  const bannerUrl = !bannerBroken ? server.banner_url : null;
  const icon = server.icon_url || SERVER_ICON_FALLBACK;
  const isFeatured = server.is_featured;
  const rating = Number(server.avg_rating || 0);
  const reviewCount = Number(server.review_count || 0);

  // Reset the broken flag whenever the URL changes so a re-uploaded banner
  // gets a fresh attempt.
  useEffect(() => {
    setBannerBroken(false);
  }, [server.banner_url]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.03, 0.3) }}
      data-testid="server-card"
      data-server-id={server.id}
      className={`relative rounded-2xl overflow-hidden bg-[#0A0A0F] border border-white/5 card-hover h-full flex flex-col ${
        isFeatured ? "neon-border" : ""
      }`}
    >
      <Link to={`/servers/${server.slug || server.id}`} className="block flex-1">
        <div
          className="relative h-28 sm:h-32 overflow-hidden"
          // Gradient is ALWAYS applied — that way if the banner img fails to
          // load (broken URL, 404, Discord CDN blip) we still show the same
          // deterministic gradient the no-banner card uses.
          style={{ background: bannerGradient(server.id) }}
        >
          {bannerUrl && (
            <img
              src={bannerUrl}
              alt=""
              className="w-full h-full object-cover opacity-70"
              loading="lazy"
              onError={() => setBannerBroken(true)}
            />
          )}
          {!bannerUrl && (
            <>
              {/* Subtle grain + glow overlay so the gradient reads richer */}
              <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.12),transparent_50%)]" />
              <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_80%_70%,rgba(255,255,255,0.08),transparent_45%)]" />
            </>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0F] via-[#0A0A0F]/60 to-transparent" />
          {isFeatured && (
            <div className="absolute top-3 right-3 flex items-center gap-1 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-xs font-semibold text-neon-cyan border border-neon-cyan/40" data-testid="featured-badge">
              <Sparkles className="w-3 h-3" />
              Featured
            </div>
          )}
        </div>

        <div className="px-5 pb-5">
          <div className="flex items-end gap-3 -mt-8 relative z-10">
            <img
              src={icon}
              alt=""
              className="w-16 h-16 rounded-2xl border-4 border-[#0A0A0F] object-cover bg-[#12121A]"
            />
            <div className="flex-1 min-w-0 pb-1">
              <h3 className="font-heading font-bold text-lg truncate" data-testid="server-name">
                {server.name}
              </h3>
              <div className="flex items-center gap-3 text-xs font-mono text-zinc-400 mt-0.5">
                <span className="inline-flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-neon-green" />
                  {server.online_count?.toLocaleString?.() ?? 0} online
                </span>
                <span className="inline-flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {server.member_count?.toLocaleString?.() ?? 0}
                </span>
              </div>
            </div>
          </div>

          <p className="mt-3 text-sm text-zinc-400 line-clamp-2 min-h-[40px]">
            {server.description}
          </p>

          <div className="mt-3 flex flex-wrap gap-1.5">
            {(server.categories || []).slice(0, 2).map((c) => (
              <Badge key={c} variant="outline" className="border-blurple/30 text-blurple bg-blurple/5 text-xs">
                {c}
              </Badge>
            ))}
            {(server.tags || []).slice(0, 3).map((t) => (
              <Badge key={t} variant="outline" className="border-white/10 text-zinc-400 bg-white/5 text-xs">
                #{t}
              </Badge>
            ))}
          </div>

          {reviewCount > 0 && (
            <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-zinc-400" data-testid="card-rating">
              <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
              <span className="font-semibold text-white">{rating.toFixed(1)}</span>
              <span className="text-zinc-500">·</span>
              <span>{reviewCount} review{reviewCount === 1 ? "" : "s"}</span>
            </div>
          )}
        </div>
      </Link>

      <div className="px-5 pb-5 flex items-center justify-between gap-2">
        <button
          data-testid="bump-button"
          onClick={(e) => {
            e.preventDefault();
            onBump?.(server);
          }}
          title="Bump this server to the top"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-neon-pink/10 hover:text-neon-pink border border-white/10 hover:border-neon-pink/40 text-sm font-semibold transition-all active:scale-95"
        >
          <Rocket className="w-4 h-4" />
          <span>Bump</span>
          {server.bumped_at && (
            <span className="text-[10px] font-mono text-zinc-500 ml-1">
              {relTime(server.bumped_at)}
            </span>
          )}
        </button>

        <a
          href={server.invite_url}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => {
            e.stopPropagation();
            trackJoinClick(server.id);
          }}
          data-testid="join-server-btn"
          className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-blurple hover:bg-blurple-hover text-white text-sm font-semibold transition-all hover:shadow-[0_0_20px_rgba(88,101,242,0.4)] active:scale-95"
        >
          Join
        </a>
      </div>
    </motion.div>
  );
}
